import '@/styles/board.css';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { addDays, addWeeks, endOfWeek, startOfDay, startOfWeek } from 'date-fns';
import {
  Calendar,
  ChevronsLeft,
  CircleUserRound,
  CornerLeftUp,
  Ellipsis,
  Flag,
  FoldHorizontal,
  Pencil,
  Plus,
  Settings2,
  Tag as TagIcon,
} from '@/components/ui/icons';
import type { CustomField, CustomFieldValue, GroupBy, Location, Status, Task, View } from '@shared/types';
import { isCompleteType, priorityMeta } from '@shared/constants';
import { between } from '@shared/ids';
import { getDb, useDb, useDbShallow, useStore, type Tables } from '@/lib/store';
import { byOrder, effectiveStatuses, statusOf, statusesForLocation, subtasksOf } from '@/lib/hierarchy';
import { subtaskCount, type TaskGroup } from '@/lib/query';
import { createTask, moveTask, reorderTask, updateTask, updateViewSettings } from '@/lib/actions';
import { dueTone, formatDue } from '@/lib/format';
import { isNeutral, readableOn } from '@/lib/color';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu } from '@/components/ui/overlays';
import { Avatar, AvatarStack } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { StatusIcon, StatusMenu, StatusPill, statusProgress } from '@/components/fields/Status';
import { TagChip, TagMenu } from '@/components/fields/Tags';
import { TaskTypeIcon } from '@/components/fields/TaskCells';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { CustomFieldCell } from '@/components/fields/CustomField';
import { openDialog } from '@/shell/dialogs';
import { ViewToolbar } from './ViewToolbar';
import { useLocationSpaceId, useViewQuery } from './useViewQuery';
import { SubtaskGlyph } from './ListView';
import { TaskContextMenu } from '@/task/TaskContextMenu';

// ---------------------------------------------------------------------------
// helpers

/** Sortable ids are `${taskId}@${groupKey}` so a task shown in two columns (multi-assignee, tags) stays unique. */
const SEP = '@';
const COL = 'col:';
const sidOf = (taskId: string, key: string) => `${taskId}${SEP}${key}`;
const taskIdOf = (sid: string) => sid.slice(0, sid.indexOf(SEP));

type Items = Record<string, string[]>;
type Adding = { key: string; top: boolean } | null;

function findContainer(id: string, items: Items): string | undefined {
  if (id.startsWith(COL)) return id.slice(COL.length);
  return Object.keys(items).find((k) => items[k].includes(id));
}

interface OverTarget {
  activeId: string;
  overId: string;
  /** pointer is past the middle of the hovered card */
  below: boolean;
}

/** Moves the dragged card into the column under the pointer; null when it is already there. */
function moveAcross(items: Items, { activeId, overId, below }: OverTarget): Items | null {
  const from = findContainer(activeId, items);
  const to = findContainer(overId, items);
  if (!from || !to || from === to || !items[to]) return null;
  const toItems = items[to];
  let idx = toItems.length;
  if (!overId.startsWith(COL)) {
    const overIndex = toItems.indexOf(overId);
    if (overIndex >= 0) idx = overIndex + (below ? 1 : 0);
  }
  return { ...items, [from]: items[from].filter((x) => x !== activeId), [to]: [...toItems.slice(0, idx), activeId, ...toItems.slice(idx)] };
}

/** Column tint color, or null for neutral / colorless groups. */
function groupColor(g: TaskGroup, groupBy: GroupBy): string | null {
  if (groupBy === 'assignee') return null; // avatar colors would make a noisy board
  const c = g.status?.color ?? g.color;
  if (!c || !c.startsWith('#') || isNeutral(c)) return null;
  return c;
}

function tintStyle(color: string | null): CSSProperties | undefined {
  return color ? ({ '--bv-c': color } as CSSProperties) : undefined;
}

/** Due date applied when a task is dropped into / created in a "Due date" column. undefined = leave as is. */
function dueForBucket(key: string): number | null | undefined {
  const now = new Date();
  switch (key) {
    case 'today':
      return startOfDay(now).getTime();
    case 'tomorrow':
      return startOfDay(addDays(now, 1)).getTime();
    case 'this_week':
      return startOfDay(endOfWeek(now)).getTime();
    case 'next_week':
      return startOfDay(addDays(startOfWeek(addWeeks(now, 1)), 1)).getTime();
    case 'later':
      return startOfDay(addDays(startOfWeek(addWeeks(now, 2)), 1)).getTime();
    case 'none':
      return null;
    default:
      return undefined;
  }
}

const isEmptyValue = (v: CustomFieldValue | undefined) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** Field changes needed to move `task` from one column to another. */
function moveChanges(db: Tables, task: Task, from: TaskGroup | undefined, to: TaskGroup, groupBy: GroupBy): { changes: Partial<Task>; listId?: string } {
  const changes: Partial<Task> = {};
  if (from && from.key === to.key) return { changes };
  if (to.status) {
    const target = effectiveStatuses(db, task.listId).find((s) => s.name.toLowerCase() === to.status!.name.toLowerCase());
    if (target && target.id !== task.statusId) changes.statusId = target.id;
  }
  switch (groupBy) {
    case 'priority':
      changes.priority = to.defaults.priority ?? null;
      break;
    case 'assignee':
      changes.assigneeIds = to.key === 'none' ? [] : [...new Set([...task.assigneeIds.filter((u) => u !== from?.key), to.key])];
      break;
    case 'taskType':
      changes.taskTypeId = (to.defaults.taskTypeId as string | null | undefined) ?? null;
      break;
    case 'tags':
      changes.tagIds = to.key === 'none' ? [] : [...new Set([...task.tagIds.filter((t) => t !== from?.key), to.key])];
      break;
    case 'dueDate': {
      const due = dueForBucket(to.key);
      if (due !== undefined) changes.dueDate = due;
      break;
    }
    case 'list':
      return { changes, listId: to.key !== task.listId ? to.key : undefined };
    default:
      if (groupBy.startsWith('cf:')) {
        const fid = groupBy.slice(3);
        const cf = db.customFields[fid];
        const cur = task.customFields[fid];
        const next: Record<string, CustomFieldValue> = { ...task.customFields };
        if (cf?.type === 'labels') {
          const arr = Array.isArray(cur) ? cur.filter((x) => x !== from?.key) : [];
          next[fid] = to.key === 'none' ? [] : [...new Set([...arr, to.key])];
        } else if (cf?.type === 'checkbox') {
          next[fid] = to.key === 'yes' ? true : null;
        } else {
          next[fid] = to.key === 'none' ? null : to.key;
        }
        if (isEmptyValue(next[fid])) delete next[fid];
        changes.customFields = next;
      }
  }
  return { changes };
}

/** Dropdown / label custom fields with a value, shown compactly on the card. */
function cardFields(db: Tables, task: Task, groupBy: GroupBy): CustomField[] {
  const out: CustomField[] = [];
  for (const [fid, v] of Object.entries(task.customFields)) {
    if (isEmptyValue(v) || groupBy === `cf:${fid}`) continue;
    const f = db.customFields[fid];
    if (f && (f.type === 'dropdown' || f.type === 'labels')) out.push(f);
  }
  return out.length > 1 ? out.sort(byOrder) : out;
}

// ---------------------------------------------------------------------------
// Card pieces

function StatusChip({ task }: { task: Task }) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  return (
    <Popover
      content={(close) => (
        <StatusMenu
          statuses={statuses}
          currentId={task.statusId}
          onPick={(s) => {
            updateTask(task.id, { statusId: s.id });
            close();
          }}
        />
      )}
    >
      <button className="bv-prop-status" title="Status">
        <StatusPill status={status} statuses={statuses} size="sm" />
      </button>
    </Popover>
  );
}

function SubtaskItem({ task, onOpen }: { task: Task; onOpen: (id: string) => void }) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  return (
    <button
      className={clsx('bv-sub', { closed: isCompleteType(status.type) })}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(task.id);
      }}
      onContextMenu={(e) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />)}
    >
      <StatusIcon type={status.type} color={status.color} progress={statusProgress(statuses, status)} />
      <span className="bv-sub-name truncate">{task.name}</span>
      {task.assigneeIds.length > 0 && <AvatarStack userIds={task.assigneeIds} size={16} max={2} />}
    </button>
  );
}

function SubtaskList({ parentId, onOpen }: { parentId: string; onOpen: (id: string) => void }) {
  const subs = useDbShallow((db) => subtasksOf(db, parentId));
  return (
    <div className="bv-subs">
      {subs.map((t) => (
        <SubtaskItem key={t.id} task={t} onOpen={onOpen} />
      ))}
    </div>
  );
}

interface CardProps {
  task: Task;
  groupBy: GroupBy;
  spaceId: string | undefined;
  subOpen: boolean;
  toggleSub: (id: string) => void;
  onOpen: (id: string) => void;
  overlay?: boolean;
}

const BoardCard = memo(function BoardCard({ task, groupBy, spaceId, subOpen, toggleSub, onOpen, overlay }: CardProps) {
  const subCount = useStore((st) => subtaskCount(st.db, task.id));
  const parentName = useDb((db) => (task.parentId ? db.tasks[task.parentId]?.name : undefined));
  const taskSpace = useDb((db) => db.lists[task.listId]?.spaceId);
  const closed = useDb((db) => isCompleteType(statusOf(db, task).type));
  const fields = useDbShallow((db) => cardFields(db, task, groupBy));
  const [renaming, setRenaming] = useState(false);
  const tagSpace = spaceId ?? taskSpace;
  const tone = dueTone(task.dueDate, closed);
  const prio = priorityMeta(task.priority);

  const commitRename = (value: string) => {
    const v = value.trim();
    if (v && v !== task.name) updateTask(task.id, { name: v });
    setRenaming(false);
  };

  return (
    <div
      className={clsx('bv-card', { overlay, renaming })}
      onClick={() => !renaming && onOpen(task.id)}
      onContextMenu={(e) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />)}
    >
      {parentName && (
        <div className="bv-card-parent">
          <CornerLeftUp size={12} />
          <span className="truncate">{parentName}</span>
        </div>
      )}
      <div className="bv-card-title">
        <TaskTypeIcon task={task} size={14} />
        {renaming ? (
          <textarea
            className="bv-card-rename"
            autoFocus
            rows={2}
            defaultValue={task.name}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename(e.currentTarget.value);
              }
              if (e.key === 'Escape') {
                e.stopPropagation();
                setRenaming(false);
              }
            }}
            onBlur={(e) => commitRename(e.target.value)}
          />
        ) : (
          <span className="bv-card-name">{task.name}</span>
        )}
      </div>

      {!overlay && !renaming && (
        <div className="bv-card-hover" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <button className="bv-card-act" title="Rename" onClick={() => setRenaming(true)}>
            <Pencil size={13} />
          </button>
          <Popover placement="bottom-end" content={(close) => <TaskContextMenu task={task} close={close} />}>
            <button className="bv-card-act" title="More">
              <Ellipsis size={14} />
            </button>
          </Popover>
        </div>
      )}

      {(task.tagIds.length > 0 || fields.length > 0) && (
        <div className="bv-card-chips">
          {task.tagIds.length > 0 && tagSpace && (
            <Popover content={() => <TagMenu spaceId={tagSpace} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
              <button className="bv-tags-btn" title="Tags">
                {task.tagIds.map((id) => (
                  <TagChip key={id} tagId={id} small />
                ))}
              </button>
            </Popover>
          )}
          {fields.map((f) => (
            <CustomFieldCell key={f.id} task={task} field={f} className="bv-cf" />
          ))}
        </div>
      )}

      <div className="bv-card-props">
        {groupBy !== 'status' && <StatusChip task={task} />}
        <Popover content={() => <PeopleMenu selected={task.assigneeIds} onChange={(ids) => updateTask(task.id, { assigneeIds: ids })} />}>
          <button className={clsx('bv-prop', task.assigneeIds.length ? 'people' : 'icon')} title={task.assigneeIds.length ? 'Assignees' : 'Add assignee'}>
            {task.assigneeIds.length ? <AvatarStack userIds={task.assigneeIds} size={20} max={4} /> : <CircleUserRound size={15} />}
          </button>
        </Popover>
        <Popover
          content={(close) => (
            <DateRangeMenu start={task.startDate} due={task.dueDate} onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })} onClose={close} />
          )}
        >
          <button className={clsx('bv-prop', task.dueDate ? `text due-${tone}` : 'icon')} title={task.dueDate ? 'Due date' : 'Add dates'}>
            {task.dueDate ? (
              <>
                <Calendar size={13} />
                <span>{formatDue(task.dueDate)}</span>
              </>
            ) : (
              <Calendar size={15} />
            )}
          </button>
        </Popover>
        <Popover
          content={(close) => (
            <PriorityMenu
              value={task.priority}
              onPick={(p) => {
                updateTask(task.id, { priority: p });
                close();
              }}
            />
          )}
        >
          <button className="bv-prop icon" title={prio ? `${prio.label} priority` : 'Set priority'}>
            {prio ? <Flag size={14} fill={prio.color} color={prio.color} /> : <Flag size={15} />}
          </button>
        </Popover>
        {task.tagIds.length === 0 && tagSpace && (
          <Popover content={() => <TagMenu spaceId={tagSpace} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
            <button className="bv-prop icon" title="Add tags">
              <TagIcon size={15} />
            </button>
          </Popover>
        )}
      </div>

      {subCount > 0 && (
        <button
          className={clsx('bv-subtoggle', { open: subOpen })}
          onClick={(e) => {
            e.stopPropagation();
            toggleSub(task.id);
          }}
        >
          <SubtaskGlyph />
          {subCount} subtask{subCount === 1 ? '' : 's'}
        </button>
      )}
      {subOpen && subCount > 0 && !overlay && <SubtaskList parentId={task.id} onOpen={onOpen} />}
    </div>
  );
});

function SortableCard({ sid, groupKey, ...card }: CardProps & { sid: string; groupKey: string }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: sid, data: { groupKey } });
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={clsx('bv-card-slot', { dragging: isDragging })} {...attributes} {...listeners} tabIndex={-1}>
      <BoardCard {...card} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline add card

function AddCard({ group, groupBy, location, top, firstOrder, onDone }: { group: TaskGroup; groupBy: GroupBy; location: Location; top: boolean; firstOrder: number | undefined; onDone: () => void }) {
  const [name, setName] = useState('');
  const [assignees, setAssignees] = useState<string[]>(group.defaults.assigneeIds ?? []);
  const [due, setDue] = useState<number | null>(() => (groupBy === 'dueDate' ? (dueForBucket(group.key) ?? null) : null));
  const [priority, setPriority] = useState<Task['priority']>(group.defaults.priority ?? null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef(name);
  nameRef.current = name;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const targetListId = useDb((db) => {
    if (group.defaults.listId) return group.defaults.listId as string;
    if (location.type === 'list') return location.id;
    const lists = Object.values(db.lists)
      .filter((l) => !l.archived && (location.type === 'folder' ? l.folderId === location.id : location.type === 'space' ? l.spaceId === location.id : db.spaces[l.spaceId]?.workspaceId === location.id))
      .sort(byOrder);
    return lists[0]?.id;
  });

  useEffect(() => inputRef.current?.focus(), []);
  // click outside (but not inside a picker popover) closes an empty card
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || rootRef.current?.contains(t) || t.closest('.popover')) return;
      if (!nameRef.current.trim()) doneRef.current();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const autoGrow = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed || !targetListId) return;
    const db = getDb();
    let statusId = group.defaults.statusId;
    if (!statusId && group.status) {
      statusId = effectiveStatuses(db, targetListId).find((s) => s.name.toLowerCase() === group.status!.name.toLowerCase())?.id;
    }
    createTask({
      listId: targetListId,
      name: trimmed,
      parentId: null,
      statusId,
      assigneeIds: assignees,
      dueDate: due,
      priority,
      taskTypeId: (group.defaults.taskTypeId as string | null | undefined) ?? null,
      tagIds: group.defaults.tagIds,
      customFields: group.defaults.customFields,
      orderIndex: top ? between(undefined, firstOrder) : undefined,
    });
    setName('');
    requestAnimationFrame(() => {
      autoGrow();
      inputRef.current?.focus();
    });
  };

  return (
    <div ref={rootRef} className="bv-addcard" onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={inputRef}
        className="bv-addcard-input"
        rows={1}
        placeholder="Task Name..."
        value={name}
        onChange={(e) => {
          setName(e.target.value.replace(/\n/g, ' '));
          autoGrow();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onDone();
          }
        }}
      />
      <div className="bv-addcard-tools">
        <Popover content={() => <PeopleMenu selected={assignees} onChange={setAssignees} />}>
          <button className={clsx('bv-prop', assignees.length ? 'people' : 'icon')} title="Assignees">
            {assignees.length ? <AvatarStack userIds={assignees} size={20} max={3} /> : <CircleUserRound size={15} />}
          </button>
        </Popover>
        <Popover content={(close) => <DateRangeMenu start={null} due={due} onChange={(_, d) => setDue(d)} onClose={close} />}>
          <button className={clsx('bv-prop', due ? 'text' : 'icon')} title="Due date">
            <Calendar size={due ? 13 : 15} />
            {due ? <span>{formatDue(due)}</span> : null}
          </button>
        </Popover>
        <Popover content={(close) => <PriorityMenu value={priority} onPick={(p) => (setPriority(p), close())} />}>
          <button className="bv-prop icon" title="Priority">
            {priority ? <PriorityFlag priority={priority} /> : <Flag size={15} />}
          </button>
        </Popover>
        <span className="spacer" />
        <button className="btn sm primary" onClick={save} disabled={!name.trim()}>
          Save
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column header

function GroupLabel({ group, groupBy, statuses }: { group: TaskGroup; groupBy: GroupBy; statuses: Status[] }) {
  if (group.status) return <StatusPill status={group.status} statuses={statuses} size="md" />;
  if (groupBy.startsWith('cf:') && group.color && group.key !== 'none') {
    return (
      <span className="bv-opt-pill" style={{ background: group.color, color: readableOn(group.color) }}>
        <span className="truncate">{group.label}</span>
      </span>
    );
  }
  let lead: React.ReactNode = null;
  if (groupBy === 'assignee') lead = group.key === 'none' ? <CircleUserRound size={16} className="faint" /> : <Avatar userId={group.key} size={20} />;
  else if (group.icon) lead = <Icon name={group.icon} size={15} color={group.color ?? 'var(--text-2)'} fill={group.icon === 'Flag' && group.color ? group.color : 'none'} />;
  else if (group.color) lead = <span className="dot" style={{ background: group.color }} />;
  return (
    <span className="bv-group-label">
      {lead}
      <span className="truncate">{group.label}</span>
    </span>
  );
}

function ColumnHeader({
  group,
  count,
  groupBy,
  statuses,
  canEditStatuses,
  location,
  onCollapse,
  onCollapseAll,
  onAddTop,
}: {
  group: TaskGroup;
  count: number;
  groupBy: GroupBy;
  statuses: Status[];
  canEditStatuses: boolean;
  location: Location;
  onCollapse: (key: string) => void;
  onCollapseAll: () => void;
  onAddTop: () => void;
}) {
  return (
    <div className="bv-col-head">
      <span className="bv-col-label">
        <GroupLabel group={group} groupBy={groupBy} statuses={statuses} />
      </span>
      <span className="bv-col-count">{count}</span>
      <span className="spacer" />
      <span className="bv-col-actions">
        <Popover
          placement="bottom-end"
          content={(close) => (
            <Menu style={{ width: 220 }}>
              <MenuItem icon={<ChevronsLeft size={15} />} label="Collapse column" onClick={() => (close(), onCollapse(group.key))} />
              <MenuItem icon={<FoldHorizontal size={15} />} label="Collapse all columns" onClick={() => (close(), onCollapseAll())} />
              <MenuItem icon={<Plus size={15} />} label="Add task" onClick={() => (close(), onAddTop())} />
              {canEditStatuses && group.status && (
                <>
                  <MenuSeparator />
                  <MenuItem
                    icon={<Settings2 size={15} />}
                    label="Edit statuses"
                    onClick={() => {
                      close();
                      openDialog({ kind: 'statuses', target: location as { type: 'space' | 'folder' | 'list'; id: string } });
                    }}
                  />
                </>
              )}
            </Menu>
          )}
        >
          <button className="bv-head-btn" title="Column options">
            <Ellipsis size={16} />
          </button>
        </Popover>
        <button className="bv-head-btn" title="Add task" onClick={onAddTop}>
          <Plus size={16} />
        </button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Columns

interface ColumnProps {
  group: TaskGroup;
  ids: string[];
  taskMap: Map<string, Task>;
  groupBy: GroupBy;
  statuses: Status[];
  location: Location;
  spaceId: string | undefined;
  canEditStatuses: boolean;
  addingTop: boolean;
  addingBottom: boolean;
  setAdding: (a: Adding) => void;
  onCollapse: (key: string) => void;
  onCollapseAll: () => void;
  subDefault: boolean;
  subOverrides: Record<string, boolean>;
  toggleSub: (id: string) => void;
  onOpen: (id: string) => void;
}

const BoardColumn = memo(function BoardColumn(p: ColumnProps) {
  const { group, ids, taskMap, setAdding } = p;
  const { setNodeRef } = useDroppable({ id: COL + group.key, data: { groupKey: group.key } });
  const color = groupColor(group, p.groupBy);
  const firstTask = ids.length ? taskMap.get(taskIdOf(ids[0])) : undefined;
  const close = useCallback(() => setAdding(null), [setAdding]);
  const addTop = () => setAdding({ key: group.key, top: true });
  return (
    <section ref={setNodeRef} className={clsx('bv-col', { tinted: !!color })} style={tintStyle(color)}>
      <ColumnHeader
        group={group}
        count={ids.length}
        groupBy={p.groupBy}
        statuses={p.statuses}
        canEditStatuses={p.canEditStatuses}
        location={p.location}
        onCollapse={p.onCollapse}
        onCollapseAll={p.onCollapseAll}
        onAddTop={addTop}
      />
      <div className="bv-col-body">
        {p.addingTop && <AddCard group={group} groupBy={p.groupBy} location={p.location} top firstOrder={firstTask?.orderIndex} onDone={close} />}
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {ids.map((sid) => {
            const t = taskMap.get(taskIdOf(sid));
            if (!t) return null;
            return (
              <SortableCard
                key={sid}
                sid={sid}
                groupKey={group.key}
                task={t}
                groupBy={p.groupBy}
                spaceId={p.spaceId}
                subOpen={p.subOverrides[t.id] ?? p.subDefault}
                toggleSub={p.toggleSub}
                onOpen={p.onOpen}
              />
            );
          })}
        </SortableContext>
        {p.addingBottom ? (
          <AddCard group={group} groupBy={p.groupBy} location={p.location} top={false} firstOrder={undefined} onDone={close} />
        ) : (
          <button className="bv-add" onClick={() => p.setAdding({ key: group.key, top: false })}>
            <Plus size={15} /> Add Task
          </button>
        )}
      </div>
    </section>
  );
});

const CollapsedColumn = memo(function CollapsedColumn({
  group,
  count,
  groupBy,
  statuses,
  onExpand,
}: {
  group: TaskGroup;
  count: number;
  groupBy: GroupBy;
  statuses: Status[];
  onExpand: (key: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: COL + group.key, data: { groupKey: group.key } });
  const color = groupColor(group, groupBy);
  return (
    <section
      ref={setNodeRef}
      className={clsx('bv-col-collapsed', { tinted: !!color, over: isOver })}
      style={tintStyle(color)}
      title={`Expand ${group.label}`}
      onClick={() => onExpand(group.key)}
    >
      <span className="bv-vert">
        <GroupLabel group={group} groupBy={groupBy} statuses={statuses} />
        <span className="bv-col-count">{count}</span>
      </span>
    </section>
  );
});

// ---------------------------------------------------------------------------

export function BoardView({ view, location: locationProp }: { view: View; location: Location }) {
  const [search, setSearch] = useState('');
  const s = view.settings;
  // keep a stable location object so memoized columns don't re-render on parent renders
  const location = useMemo<Location>(() => ({ type: locationProp.type, id: locationProp.id }), [locationProp.type, locationProp.id]);
  const q = useViewQuery(view, location, search, { includeEmptyStatuses: s.showEmptyStatuses !== false });
  const statuses = useDbShallow((db) => statusesForLocation(db, location));
  const spaceId = useLocationSpaceId(location);
  const groupBy = s.groupBy;
  const canEditStatuses = groupBy === 'status' && location.type !== 'workspace';

  const openTask = useOpenTask();
  const openRef = useRef(openTask);
  openRef.current = openTask;
  const onOpen = useCallback((id: string) => openRef.current(id), []);

  // collapsed columns
  const collapsed = useMemo(() => new Set(s.collapsedGroups), [s.collapsedGroups]);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const groupsRef = useRef(q.groups);
  groupsRef.current = q.groups;
  const toggleCollapse = useCallback(
    (key: string) => {
      const cur = getDb().views[view.id]?.settings.collapsedGroups ?? [];
      updateViewSettings(view.id, { collapsedGroups: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
    },
    [view.id],
  );
  const collapseAll = useCallback(() => updateViewSettings(view.id, { collapsedGroups: groupsRef.current.map((g) => g.key) }), [view.id]);

  // subtask lists on cards
  const subDefault = s.subtasks === 'expanded';
  const subDefaultRef = useRef(subDefault);
  subDefaultRef.current = subDefault;
  const [subOverrides, setSubOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => setSubOverrides({}), [s.subtasks]);
  const toggleSub = useCallback((id: string) => setSubOverrides((prev) => ({ ...prev, [id]: !(prev[id] ?? subDefaultRef.current) })), []);

  // inline add
  const [adding, setAdding] = useState<Adding>(null);
  const addFromToolbar = () => {
    const g = q.groups.find((x) => !collapsed.has(x.key));
    setAdding({ key: g?.key ?? '__new', top: true });
  };

  // drag & drop (multi-container sortable)
  const baseItems = useMemo(() => {
    const m: Items = {};
    for (const g of q.groups) m[g.key] = g.tasks.map((t) => sidOf(t.id, g.key));
    return m;
  }, [q.groups]);
  const taskMap = useMemo(() => {
    const m = new Map<string, Task>();
    for (const g of q.groups) for (const t of g.tasks) m.set(t.id, t);
    return m;
  }, [q.groups]);
  const [dragItems, setDragItems] = useState<Items | null>(null);
  const [activeSid, setActiveSid] = useState<string | null>(null);
  const items = dragItems ?? baseItems;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const fromKeyRef = useRef<string | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  // Cross-column moves change the layout, which changes collisions. Allow at most one move per
  // short window (the latest skipped target is applied when it ends) so the board can't ping-pong.
  const moveLock = useRef(false);
  const pendingOver = useRef<OverTarget | null>(null);
  const dragging = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const collision: CollisionDetection = useCallback((args) => {
    // pointer-based: while the pointer is in a gap between columns keep the last target
    let overId = getFirstCollision(pointerWithin(args), 'id');
    if (overId == null) {
      if (lastOverId.current != null) return [{ id: lastOverId.current }];
      overId = getFirstCollision(rectIntersection(args), 'id');
      if (overId == null) return [];
    }
    const id = String(overId);
    if (id.startsWith(COL)) {
      const key = id.slice(COL.length);
      const ids = itemsRef.current[key] ?? [];
      if (ids.length && !collapsedRef.current.has(key)) {
        const inner = closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => ids.includes(String(c.id))) });
        if (inner.length) overId = inner[0].id;
      }
    }
    lastOverId.current = overId;
    return [{ id: overId }];
  }, []);

  const applyOver = (target: OverTarget) => {
    if (!dragging.current) return;
    if (moveLock.current) {
      pendingOver.current = target;
      return;
    }
    const next = moveAcross(itemsRef.current, target);
    if (!next) return;
    moveLock.current = true;
    itemsRef.current = next;
    setDragItems(next);
    setTimeout(() => {
      moveLock.current = false;
      const p = pendingOver.current;
      pendingOver.current = null;
      if (p) applyOver(p);
    }, 50);
  };

  const resetDrag = () => {
    dragging.current = false;
    pendingOver.current = null;
    setActiveSid(null);
    setDragItems(null);
    fromKeyRef.current = null;
    lastOverId.current = null;
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    dragging.current = true;
    moveLock.current = false;
    setActiveSid(id);
    fromKeyRef.current = findContainer(id, baseItems) ?? null;
    itemsRef.current = baseItems;
    setDragItems(baseItems);
  };

  const overTarget = (active: DragOverEvent['active'], over: NonNullable<DragOverEvent['over']>): OverTarget => {
    const translated = active.rect.current.translated;
    return { activeId: String(active.id), overId: String(over.id), below: !!translated && translated.top > over.rect.top + over.rect.height / 2 };
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (over) applyOver(overTarget(active, over));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    let its = itemsRef.current;
    const fromKey = fromKeyRef.current;
    resetDrag();
    if (!over) return;
    // apply a cross-column move that may still be pending
    its = moveAcross(its, overTarget(active, over)) ?? its;
    const activeId = String(active.id);
    const overId = String(over.id);
    const toKey = findContainer(activeId, its);
    if (!toKey) return;
    let list = its[toKey];
    if (!overId.startsWith(COL) && findContainer(overId, its) === toKey) {
      const a = list.indexOf(activeId);
      const b = list.indexOf(overId);
      if (a >= 0 && b >= 0 && a !== b) list = arrayMove(list, a, b);
    }
    const taskId = taskIdOf(activeId);
    const db = getDb();
    const task = db.tasks[taskId];
    const toGroup = q.groups.find((g) => g.key === toKey);
    if (!task || !toGroup) return;
    const fromGroup = q.groups.find((g) => g.key === fromKey);
    const { changes, listId } = moveChanges(db, task, fromGroup, toGroup, groupBy);
    if (s.sort.length) {
      // sorted views: position is derived, only apply the column change
      if (Object.keys(changes).length) updateTask(taskId, changes);
    } else {
      const others = list.filter((x) => x === activeId || taskIdOf(x) !== taskId);
      const idx = others.indexOf(activeId);
      const before = idx > 0 ? taskMap.get(taskIdOf(others[idx - 1])) : undefined;
      const after = idx >= 0 && idx < others.length - 1 ? taskMap.get(taskIdOf(others[idx + 1])) : undefined;
      const unchanged = fromKey === toKey && baseItems[toKey]?.indexOf(activeId) === list.indexOf(activeId);
      if (!unchanged || Object.keys(changes).length) reorderTask(taskId, before, after, changes);
    }
    if (listId) moveTask(taskId, listId);
  };

  const activeTask = activeSid ? taskMap.get(taskIdOf(activeSid)) : undefined;
  const activeKey = activeSid ? findContainer(activeSid, items) : undefined;
  const activeGroup = activeKey ? q.groups.find((g) => g.key === activeKey) : undefined;
  const ghostColor = activeGroup ? groupColor(activeGroup, groupBy) : null;

  const newGroup = useMemo<TaskGroup>(() => ({ key: '__new', label: 'Tasks', defaults: {}, tasks: [] }), []);

  return (
    <div className="bv">
      <ViewToolbar
        view={view}
        location={location}
        search={search}
        onSearch={setSearch}
        onAddTask={addFromToolbar}
        features={{ group: true, subtasks: true, columns: false, sort: true }}
      />
      <div className="bv-scroll">
        <DndContext
          sensors={sensors}
          collisionDetection={collision}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={resetDrag}
        >
          <div className="bv-board">
            {q.groups.map((g) =>
              collapsed.has(g.key) ? (
                <CollapsedColumn key={g.key} group={g} count={(items[g.key] ?? []).length} groupBy={groupBy} statuses={statuses} onExpand={toggleCollapse} />
              ) : (
                <BoardColumn
                  key={g.key}
                  group={g}
                  ids={items[g.key] ?? []}
                  taskMap={taskMap}
                  groupBy={groupBy}
                  statuses={statuses}
                  location={location}
                  spaceId={spaceId}
                  canEditStatuses={canEditStatuses}
                  addingTop={adding?.key === g.key && adding.top}
                  addingBottom={adding?.key === g.key && !adding.top}
                  setAdding={setAdding}
                  onCollapse={toggleCollapse}
                  onCollapseAll={collapseAll}
                  subDefault={subDefault}
                  subOverrides={subOverrides}
                  toggleSub={toggleSub}
                  onOpen={onOpen}
                />
              ),
            )}
            {q.groups.length === 0 && (
              <div className="bv-empty">
                <div className="bv-empty-title">No tasks found</div>
                <div className="faint">Try changing filters, or create a task.</div>
                {adding?.key === '__new' ? (
                  <AddCard group={newGroup} groupBy={groupBy} location={location} top={false} firstOrder={undefined} onDone={() => setAdding(null)} />
                ) : (
                  <button className="btn primary" style={{ marginTop: 12 }} onClick={() => setAdding({ key: '__new', top: false })}>
                    <Plus size={14} /> Add Task
                  </button>
                )}
              </div>
            )}
            {canEditStatuses && q.groups.length > 0 && (
              <button className="bv-add-status" onClick={() => openDialog({ kind: 'statuses', target: location as { type: 'space' | 'folder' | 'list'; id: string } })}>
                <Plus size={15} /> Add status
              </button>
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              <div className={clsx('bv-ghost', { tinted: !!ghostColor })} style={tintStyle(ghostColor)}>
                <BoardCard task={activeTask} groupBy={groupBy} spaceId={spaceId} subOpen={false} toggleSub={toggleSub} onOpen={onOpen} overlay />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
